<template>
  <!-- Composant invisible qui gère l'authentification -->
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, watch } from 'vue'
import { useAuthStore } from '../stores/authStore'
import { useRouter } from 'vue-router'

const authStore = useAuthStore()
const router = useRouter()

// Gestionnaire pour les changements de visibilité de la page
const handleVisibilityChange = async () => {
  if (!document.hidden && authStore.token && !authStore.user && !authStore.isTokenExpired) {
    console.log('🔄 Page redevenue visible, vérification de l\'authentification...')
    await authStore.checkAndRestoreAuth()
  }
}

// Gestionnaire pour les changements de focus de la fenêtre
const handleFocus = async () => {
  if (authStore.token && !authStore.user && !authStore.isTokenExpired) {
    console.log('🔄 Fenêtre refocalisée, vérification de l\'authentification...')
    await authStore.checkAndRestoreAuth()
  }
}

// Gestionnaire pour les événements de stockage (synchronisation entre onglets)
const handleStorageChange = (event: StorageEvent) => {
  if (event.key === 'token' || event.key === 'user-data') {
    console.log('🔄 Changement de stockage détecté, synchronisation...')
    // Recharger l'état d'authentification depuis le localStorage
    authStore.initializeFromStorage()
  }
}

// Surveiller les changements d'état d'authentification
watch(() => authStore.isAuthenticated, (isAuthenticated, wasAuthenticated) => {
  if (wasAuthenticated && !isAuthenticated) {
    console.log('🔒 Utilisateur déconnecté')
    // Rediriger vers la page d'accueil si nécessaire
    if (router.currentRoute.value.meta.requiresAuth) {
      router.push('/')
    }
  } else if (!wasAuthenticated && isAuthenticated) {
    console.log('🔓 Utilisateur connecté')
  }
})

// Surveiller les changements de token
watch(() => authStore.token, (newToken, oldToken) => {
  if (newToken && newToken !== oldToken) {
    console.log('🔑 Nouveau token reçu')
    // Sauvegarder dans le localStorage
    authStore.saveToStorage()
  }
})

// Vérification périodique de l'authentification (toutes les 5 minutes)
let authCheckInterval: NodeJS.Timeout | null = null

const startPeriodicAuthCheck = () => {
  authCheckInterval = setInterval(async () => {
    if (authStore.token && !authStore.isTokenExpired) {
      console.log('⏰ Vérification périodique de l\'authentification...')
      await authStore.fetchCurrentUser()
    }
  }, 5 * 60 * 1000) // 5 minutes
}

const stopPeriodicAuthCheck = () => {
  if (authCheckInterval) {
    clearInterval(authCheckInterval)
    authCheckInterval = null
  }
}

onMounted(async () => {
  console.log('🔐 AuthManager monté')
  
  // Initialiser l'authentification si ce n'est pas déjà fait
  if (!authStore.isInitialized) {
    await authStore.initializeAuth()
  }
  
  // Démarrer la vérification périodique
  startPeriodicAuthCheck()
  
  // Ajouter les écouteurs d'événements
  document.addEventListener('visibilitychange', handleVisibilityChange)
  window.addEventListener('focus', handleFocus)
  window.addEventListener('storage', handleStorageChange)
})

onUnmounted(() => {
  console.log('🔐 AuthManager démonté')
  
  // Arrêter la vérification périodique
  stopPeriodicAuthCheck()
  
  // Supprimer les écouteurs d'événements
  document.removeEventListener('visibilitychange', handleVisibilityChange)
  window.removeEventListener('focus', handleFocus)
  window.removeEventListener('storage', handleStorageChange)
})
</script>
